import {
  ArrowLeft, Pencil, FileText, CheckCircle2, AlertOctagon,
  Plus, RefreshCw, Send, Paperclip, MailOpen, History as HistoryIcon,
  PackageCheck, ExternalLink,
} from 'lucide-react';

// Botao/link pra abrir anexo em nova aba. Usado nas linhas de anexo
// (nota, auxiliar, complementares) na sessao de detalhes.
function AnexoLink({ url, label = 'Abrir' }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px', borderRadius: 6,
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        color: 'var(--ink)',
        fontSize: 12.5, fontWeight: 600, textDecoration: 'none',
        fontFamily: 'inherit',
        transition: 'all .12s',
      }}
      onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface-3)'; }}
      onMouseOut={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; }}
    >
      <ExternalLink size={12} />
      {label}
    </a>
  );
}
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { StatusPill } from './ui/StatusPill';
import { formatBRL, formatDateBR } from '../lib/formatters';
import { useSolicitacaoEventos } from '../hooks/useSolicitacaoEventos';

// Renderiza um evento da timeline com icone, frase humanizada e
// destacando detalhes relevantes. Map ev -> { Icon, color, label(detalhes) }.
function describeEvento(evt) {
  const d = evt.detalhes || {};
  const recip = Array.isArray(d.destinatarios) ? d.destinatarios.length : null;
  switch (evt.evento) {
    case 'criada':
      return {
        Icon: Plus,
        color: 'var(--primary)',
        text: 'Lançamento criado',
      };
    case 'editada':
      return {
        Icon: Pencil,
        color: 'var(--info)',
        text: 'Lançamento editado',
      };
    case 'status_alterado':
      return {
        Icon: RefreshCw,
        color: 'var(--info)',
        text: `Status alterado: ${d.de || '?'} → ${d.para || '?'}${d.automatico ? ' (automático)' : ''}`,
      };
    case 'nf_anexada':
      return {
        Icon: Paperclip,
        color: 'var(--info)',
        text: 'Anexo da NF emitida adicionado',
      };
    case 'espelho_enviado_mailto':
      return {
        Icon: MailOpen,
        color: 'var(--muted)',
        text: `Espelho preparado para envio via cliente de e-mail${recip ? ` (${recip} destinatário${recip > 1 ? 's' : ''})` : ''}`,
      };
    case 'espelho_enviado_servidor':
      return {
        Icon: Send,
        color: 'var(--info)',
        text: `Espelho enviado pelo servidor${recip ? ` para ${recip} destinatário${recip > 1 ? 's' : ''}` : ''}${d.para_emissao ? ' (direto para cobrança/emissão)' : ''}`,
      };
    case 'espelho_aprovado':
      return {
        Icon: CheckCircle2,
        color: 'var(--primary)',
        text: 'Espelho APROVADO pelo tomador',
      };
    case 'espelho_recusado': {
      const motivo = String(d.motivo || '').slice(0, 120);
      return {
        Icon: AlertOctagon,
        color: 'var(--danger)',
        text: `Espelho RECUSADO pelo tomador${motivo ? ': "' + motivo + (d.motivo && d.motivo.length > 120 ? '…' : '') + '"' : ''}`,
      };
    }
    case 'nf_emitida_enviada':
      return {
        Icon: Send,
        color: 'var(--primary)',
        text: `E-mail de NF emitida disparado${recip ? ` para ${recip} destinatário${recip > 1 ? 's' : ''}` : ''}`,
      };
    case 'nf_recebida_confirmada':
      return {
        Icon: PackageCheck,
        color: 'var(--primary)',
        text: 'Recebimento da NF confirmado pelo tomador',
      };
    default:
      return {
        Icon: HistoryIcon,
        color: 'var(--muted)',
        text: evt.evento,
      };
  }
}

// Formata 'YYYY-MM-DDTHH:MM:SS.sssZ' -> 'DD/MM/YYYY HH:MM' (BR local)
function formatTimestampBR(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mn = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yy} ${hh}:${mn}`;
  } catch { return iso; }
}

export function DetailsModal({ open, onClose, solicitacao, empresa, tomador, servico, onPdf, onEdit }) {
  // Hook precisa rodar SEMPRE (rules of hooks). Quando solicitacao=null
  // ou modal fechado, passamos null e o hook retorna data vazio.
  const { data: eventos, loading: eventosLoading } = useSolicitacaoEventos(
    open && solicitacao?.id ? solicitacao.id : null
  );
  if (!solicitacao) return null;
  const Row = ({ label, value, mono }) => (
    <div style={{ display: 'flex', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ width: 200, fontSize: 12, color: 'var(--muted)', fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{
        flex: 1, fontSize: 13.5,
        fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        {value || '—'}
      </div>
    </div>
  );
  return (
    <Modal open={open} onClose={onClose} size="lg"
      title={solicitacao.codigo_unico}
      subtitle={`PI ${solicitacao.pi}`}
      footer={
        <>
          <Button variant="secondary" icon={ArrowLeft} onClick={onClose}>Voltar</Button>
          <Button variant="secondary" icon={Pencil} onClick={() => { onClose(); onEdit(solicitacao); }}>Editar</Button>
          <Button icon={FileText} onClick={() => onPdf(solicitacao)}>Visualizar PDF</Button>
        </>
      }
    >
      <div style={{
        background: 'var(--primary-soft)', border: '1px solid var(--primary-line)',
        borderRadius: 'var(--radius)', padding: 16, marginBottom: 18, display: 'flex',
        justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--primary)', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '.06em' }}>Valor</div>
          <div className="font-money" style={{ fontSize: 28, fontWeight: 700,
            color: 'var(--ink)' }}>
            {formatBRL(solicitacao.valor)}
          </div>
        </div>
        <StatusPill status={solicitacao.status} />
      </div>

      {/* Feedback do tomador (vem do botao 'Aprovar/Recusar' no email do
          espelho). Mostra so se houver registro. Aprovacao verde, recusa
          vermelha com motivo destacado. */}
      {solicitacao.aprovado_em && (
        <div style={{
          background: 'var(--primary-soft)',
          border: '1px solid var(--primary-line)',
          borderRadius: 'var(--radius)',
          padding: 14, marginBottom: 14,
          display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <CheckCircle2 size={20} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--primary)', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>
              Espelho aprovado pelo tomador
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>
              {formatTimestampBR(solicitacao.aprovado_em)}
              {solicitacao.aprovado_por_email && <> · <span className="font-mono" style={{ fontSize: 12 }}>{solicitacao.aprovado_por_email}</span></>}
            </div>
          </div>
        </div>
      )}
      {solicitacao.motivo_rejeicao && (
        <div style={{
          background: 'var(--danger-soft)',
          border: '1px solid rgba(255,80,80,0.35)',
          borderRadius: 'var(--radius)',
          padding: 14, marginBottom: 14,
          display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <AlertOctagon size={20} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>
              Espelho recusado pelo tomador
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
              {formatTimestampBR(solicitacao.rejeitado_em)}
              {solicitacao.rejeitado_por_email && <> · <span className="font-mono" style={{ fontSize: 11.5 }}>{solicitacao.rejeitado_por_email}</span></>}
            </div>
            <div style={{
              fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              padding: '8px 10px', background: 'var(--surface-2)',
              borderRadius: 8, border: '1px solid var(--border)',
            }}>
              {solicitacao.motivo_rejeicao}
            </div>
          </div>
        </div>
      )}
      {solicitacao.recebido_em && (() => {
        // Distingue entre confirmação externa (tomador clicou no email)
        // vs registro interno (equipe mudou o status pra 'Recebido' no app).
        // Heurística: se recebido_por_email termina em @etus.com.br, é interno.
        const email = solicitacao.recebido_por_email || '';
        const isInterno = /@etus\.com\.br\s*$/i.test(email);
        const titulo = isInterno
          ? 'Data de recebimento'
          : 'Recebimento confirmado pelo tomador';
        return (
          <div style={{
            background: 'var(--primary-soft)',
            border: '1px solid var(--primary-line)',
            borderRadius: 'var(--radius)',
            padding: 14, marginBottom: 14,
            display: 'flex', gap: 12, alignItems: 'flex-start',
          }}>
            <PackageCheck size={20} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: 'var(--primary)', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>
                {titulo}
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                {formatTimestampBR(solicitacao.recebido_em)}
                {email && <> · <span className="font-mono" style={{ fontSize: 12 }}>{email}</span></>}
              </div>
            </div>
          </div>
        );
      })()}

      {solicitacao.serie_total > 1 && (
        <Row
          label="Série"
          value={
            <span>
              {solicitacao.serie_tipo === 'parcelamento' ? 'Parcela' : 'Recorrência'}
              {' '}
              <strong style={{ color: 'var(--primary)' }}>
                {solicitacao.serie_posicao} de {solicitacao.serie_total}
              </strong>
              {solicitacao.serie_id && (
                <>
                  {' · '}
                  <span className="font-mono" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                    id {String(solicitacao.serie_id).slice(0, 8)}
                  </span>
                </>
              )}
            </span>
          }
        />
      )}
      <Row label="Empresa" value={empresa ? `${empresa.nome_fantasia || empresa.razao_social} · ${empresa.cnpj}` : '—'} />
      <Row label="Tomador" value={tomador ? `${tomador.nome_fantasia || tomador.razao_social} · ${tomador.cnpj}` : '—'} />
      <Row label="Serviço" value={servico ? `${servico.codigo} — ${servico.descricao}` : '—'} />
      <Row label="Data da solicitação" value={formatDateBR(solicitacao.data_solicitacao)} />
      <Row label="Competência" value={formatDateBR(solicitacao.data_prestacao_servico)} />
      <Row label="Descrição" value={solicitacao.descricao} />
      <Row label="Solicitante" value={solicitacao.solicitante} />
      <Row label="Nº da nota" value={solicitacao.numero_nota || '— a emitir'} mono />
      <Row label="Impostos retidos" value={(solicitacao.impostos_retidos || []).join(' · ')} />
      <Row label="Info. complementares" value={solicitacao.informacoes_complementares} />
      <Row label="E-mails para envio" value={solicitacao.emails_envio} />
      <Row label="Intermediário" value={solicitacao.possui_intermediario ? 'Sim' : 'Não'} />
      {solicitacao.possui_intermediario && (
        <>
          <Row label="CNPJ do intermediário" value={solicitacao.intermediario_cnpj} mono />
          <Row label="Razão social do intermediário" value={solicitacao.intermediario_razao_social} />
        </>
      )}
      <Row label="Anexo da nota emitida" value={
        solicitacao.anexo_nota_url
          ? <AnexoLink url={solicitacao.anexo_nota_url} label="Abrir nota" />
          : '—'
      } />
      {solicitacao.anexo_intermediario_url && (
        <Row label="PI / Documento auxiliar" value={
          <AnexoLink url={solicitacao.anexo_intermediario_url} label="Abrir arquivo" />
        } />
      )}
      {solicitacao.anexo_complementar_1_url && (
        <Row label="Relatórios" value={
          <AnexoLink url={solicitacao.anexo_complementar_1_url} label="Abrir relatórios" />
        } />
      )}
      {solicitacao.anexo_complementar_2_url && (
        <Row label="Cartas" value={
          <AnexoLink url={solicitacao.anexo_complementar_2_url} label="Abrir cartas" />
        } />
      )}
      {Array.isArray(solicitacao.documentos_auxiliares) && solicitacao.documentos_auxiliares.length > 0 && (
        <Row label="Documentos auxiliares" value={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            {solicitacao.documentos_auxiliares.map((d, i) => d?.url && (
              <AnexoLink key={i} url={d.url} label={d.filename || `arquivo ${i + 1}`} />
            ))}
          </div>
        } />
      )}

      {/* ── Trilha de auditoria ────────────────────────────────────── */}
      <div style={{ marginTop: 24 }}>
        <div style={{
          fontSize: 11, color: 'var(--muted)', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '.06em',
          marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <HistoryIcon size={13} /> Histórico
        </div>

        {eventosLoading && (
          <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Carregando…</div>
        )}

        {!eventosLoading && eventos.length === 0 && (
          <div style={{
            fontSize: 12.5, color: 'var(--muted)', fontStyle: 'italic',
            padding: '10px 12px', background: 'var(--surface-2)',
            border: '1px dashed var(--border)', borderRadius: 8,
          }}>
            Nenhum evento registrado ainda. (Lançamentos antigos — anteriores à
            implementação da auditoria — também não têm histórico.)
          </div>
        )}

        {!eventosLoading && eventos.length > 0 && (
          <div style={{ position: 'relative', paddingLeft: 18 }}>
            {/* Linha vertical da timeline */}
            <div style={{
              position: 'absolute', left: 7, top: 8, bottom: 8,
              width: 1, background: 'var(--border)',
            }} />

            {eventos.map((evt) => {
              const { Icon, color, text } = describeEvento(evt);
              const ator = evt.ator_email
                ? evt.ator_email
                : (evt.ator_tipo === 'sistema' ? 'sistema' : '—');
              return (
                <div key={evt.id} style={{
                  position: 'relative', paddingBottom: 14,
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                }}>
                  {/* Marker da timeline */}
                  <div style={{
                    position: 'absolute', left: -18,
                    width: 16, height: 16, borderRadius: '50%',
                    background: 'var(--surface)',
                    border: `2px solid ${color}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    top: 1,
                  }}>
                    <Icon size={9} style={{ color }} strokeWidth={2.5} />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, color: 'var(--ink)', lineHeight: 1.4,
                      wordBreak: 'break-word',
                    }}>
                      {text}
                    </div>
                    <div style={{
                      fontSize: 11.5, color: 'var(--muted)', marginTop: 2,
                      display: 'flex', flexWrap: 'wrap', gap: '0 8px',
                    }}>
                      <span className="font-mono" style={{ color: 'var(--ink-2)' }}>
                        {ator}
                      </span>
                      <span>·</span>
                      <span>{formatTimestampBR(evt.criado_em)}</span>
                      {evt.ator_tipo === 'tomador' && (
                        <span style={{
                          padding: '0 6px', borderRadius: 4,
                          background: 'var(--surface-2)',
                          fontSize: 10, fontWeight: 600,
                          textTransform: 'uppercase', letterSpacing: '.04em',
                        }}>
                          tomador
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
